import * as MetadataView from '../features/metadataView';

/**
 * Process and filter data based on applied filters
 * @param {Array} data Raw data array
 * @param {Array} columns Column definitions
 * @param {Object} filters Applied filters
 * @param {boolean} showAllImageColumns Whether to show all image columns
 * @returns {Object} Processed data and columns
 */
export const processData = (data, columns, filters, showAllImageColumns) => {
  if (!data.length || !columns.length) return { displayColumns: [], processedData: [], metadataColumnCount: 0 };

  // If metadata view is enabled (and feature is on), return metadata columns
  if (MetadataView.ENABLED && filters.showMetadataView) {
    return MetadataView.processMetadataView(data);
  }

  // Initialize variables
  let processedRows = [...data];
  let processedColumns = [...columns];
  let metadataColumnCount = 0;

  // Debug columns (raw JSON-LD blobs, *__json escape hatches, redundant address.* sub-fields,
  // provenance) are extracted losslessly but hidden from the table by default. They carry an
  // explicit `debug: true` flag set when the column is built (structured-data extraction). Flip
  // "Show debug columns" on to reveal them. Filtered first so all downstream steps inherit the
  // trimmed set. Mirrors the extension DataTable.
  if (!filters.showDebugColumns) {
    processedColumns = processedColumns.filter((col) => !col?.debug);
  }

  // Identify image columns
  const imageColumns = processedColumns.filter((col) => col.type === "image");

  // Create a new set of columns to display
  let displayColumns = [...processedColumns];

  // If we have image columns and should not include images, filter them out
  if (imageColumns.length > 0 && !filters.includeImages) {
    displayColumns = displayColumns.filter((col) => col.type !== "image");
  }

  // Create final display columns with image previews first
  let finalDisplayColumns = [];

  // Handle image column grouping (collapsed vs expanded)
  if (imageColumns.length > 0 && filters.includeImages) {
    // Count the total number of non-empty image values across all rows and columns
    const totalNonEmptyImages = processedRows.reduce((count, row) => {
      const nonEmptyImagesInRow = imageColumns.filter((col) => row.data[col.id] && typeof row.data[col.id] === "string").length;
      return count + nonEmptyImagesInRow;
    }, 0);

    if (filters.imageColumnsExpanded) {
      // EXPANDED: Show collapse toggle column with preview, keep individual image columns visible
      finalDisplayColumns.push({
        id: "image_group_toggle",
        name: `Images (${imageColumns.length})`,
        type: "image_group_toggle",
        imageCount: totalNonEmptyImages > 0 ? totalNonEmptyImages : 0,
        imageColumnCount: imageColumns.length,
        imageColumns: imageColumns, // Store for preview rendering
      });
      // Keep image columns in displayColumns (don't filter them out)
    } else {
      // COLLAPSED: Show preview column, hide individual image columns
      displayColumns = displayColumns.filter((col) => col.type !== "image");
      finalDisplayColumns.push({
        id: "image_previews",
        name: `Images (${imageColumns.length})`,
        type: "image_previews",
        imageCount: totalNonEmptyImages > 0 ? totalNonEmptyImages : 0,
        imageColumnCount: imageColumns.length,
        imageColumns: imageColumns, // Store original image columns for preview rendering
      });
    }
  }

  // Add all other columns
  finalDisplayColumns = [...finalDisplayColumns, ...displayColumns];

  return {
    displayColumns: finalDisplayColumns,
    processedData: processedRows,
    metadataColumnCount,
  };
};
